import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Search, 
  MessageCircle, 
  Mail, 
  Phone, 
  ChevronDown, 
  ChevronRight,
  Heart,
  Shield,
  Zap,
  Users,
  Settings,
  CreditCard,
  Video,
  Headphones,
  Clock,
  Star,
  AlertCircle,
  CheckCircle,
  HelpCircle
} from 'lucide-react';
import SEOHead from './SEOHead';

const SupportPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [openFAQ, setOpenFAQ] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    category: '',
    message: ''
  });

  const faqSections = [
    {
      title: 'Getting Started',
      icon: <Users className="w-5 h-5" />,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      faqs: [
        {
          question: 'How do I create my profile?',
          answer: 'Creating your profile is easy! Click "Sign Up", enter your basic information, upload photos, and let our AI help optimize your profile for better matches. The process takes about 5 minutes.'
        },
        {
          question: 'What is AI-powered speed dating?',
          answer: 'Our AI analyzes your preferences, personality, and conversation style to match you with compatible singles for 3-5 minute speed dates. It\'s like traditional speed dating, but smarter!'
        },
        {
          question: 'How do I set up my first speed date?',
          answer: 'Once your profile is complete, browse available speed dating events, select one that interests you, and reserve your spot. Our AI will automatically match you with compatible participants.'
        }
      ]
    },
    {
      title: 'Speed Dating Features',
      icon: <Clock className="w-5 h-5" />,
      color: 'text-rose-600',
      bgColor: 'bg-rose-50',
      faqs: [
        {
          question: 'What is the date duration?',
          answer: 'Each speed date lasts exactly 3-5 minutes - the perfect amount of time to make a great first impression and decide if you\'d like to connect further.'
        },
        {
          question: 'What happens after a speed date?',
          answer: 'After each session, you can mark if you\'re interested in the person. If both people mark "Yes," it\'s a match! You can then start chatting and plan a longer conversation.'
        },
        {
          question: 'Can I extend a conversation if we\'re connecting well?',
          answer: 'Premium users can request a 2-minute extension during active sessions. Both participants must agree to the extension for it to activate.'
        }
      ]
    },
    {
      title: 'AI Features',
      icon: <Zap className="w-5 h-5" />,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
      faqs: [
        {
          question: 'How does the AI matching work?',
          answer: 'Our AI analyzes your interests, conversation style, values, and preferences to find highly compatible matches. It learns from your interactions to improve recommendations over time.'
        },
        {
          question: 'What are AI-powered icebreakers?',
          answer: 'Our AI suggests personalized icebreakers based on shared interests and compatibility factors. These help you start meaningful conversations during speed dating sessions.'
        },
        {
          question: 'Can I turn off AI features?',
          answer: 'Yes! You can customize which AI features you want to use in your account settings. However, we recommend keeping them on for the best matching experience.'
        }
      ]
    },
    {
      title: 'Safety & Privacy',
      icon: <Shield className="w-5 h-5" />,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      faqs: [
        {
          question: 'Is video speed dating safe?',
          answer: 'Absolutely! All sessions are monitored by our AI safety system. You can report inappropriate behavior instantly, and all conversations are recorded (but not stored) for safety purposes.'
        },
        {
          question: 'How do I report someone?',
          answer: 'Click the "Report" button during or after any conversation. Our safety team reviews all reports within 24 hours and takes appropriate action.'
        },
        {
          question: 'What information is shared with other users?',
          answer: 'Only your profile information that you choose to share is visible to other users. We never share personal details like email, phone, or location data.'
        }
      ]
    },
    {
      title: 'Technical Support',
      icon: <Settings className="w-5 h-5" />,
      color: 'text-indigo-600',
      bgColor: 'bg-indigo-50',
      faqs: [
        {
          question: 'What browsers are supported?',
          answer: 'Shot Clock Dating works best on Chrome, Firefox, Safari, and Edge (latest versions). Make sure to allow camera and microphone access for video dating.'
        },
        {
          question: 'I\'m having audio/video issues during speed dating',
          answer: 'Check your internet connection, ensure camera/microphone permissions are enabled, and try refreshing the page. For persistent issues, use our live chat support.'
        },
        {
          question: 'Can I use Shot Clock Dating on mobile?',
          answer: 'Yes! Our Progressive Web App (PWA) works perfectly on mobile. Add it to your home screen for an app-like experience.'
        }
      ]
    },
    {
      title: 'Subscription & Billing',
      icon: <CreditCard className="w-5 h-5" />,
      color: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
      faqs: [
        {
          question: 'What\'s included in the Premium plan?',
          answer: 'Premium includes unlimited speed dates, AI-powered icebreakers, advanced matching filters, and conversation insights.'
        },
        {
          question: 'How do I cancel my subscription?',
          answer: 'Go to Account Settings > Subscription > Cancel Subscription. Your premium features remain active until the end of your billing period.'
        },
        {
          question: 'Do you offer refunds?',
          answer: (
            <>
              We offer a 7-day money-back guarantee for new subscribers. Contact{' '}
              <a 
                href="mailto:admin@shotclockdating.com"
                className="text-rose-600 hover:text-rose-700 underline font-medium"
              >
                admin@shotclockdating.com
              </a>
              {' '}with your request and account details.
            </>
          )
        }
      ]
    }
  ];

  const contactOptions = [
    {
      icon: <Mail className="w-6 h-6" />,
      title: 'Email Support',
      description: (
        <a 
          href="mailto:admin@shotclockdating.com"
          className="text-gray-600 hover:text-rose-600 transition-colors underline"
        >
          admin@shotclockdating.com
        </a>
      ),
      action: 'Send Email',
      actionHandler: () => window.open('mailto:admin@shotclockdating.com', '_blank'),
      color: 'bg-rose-500 hover:bg-rose-600'
    }
  ];

  const quickTips = [
    {
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      title: 'Profile Optimization',
      description: 'Upload 3-5 high-quality photos and fill out all profile sections for better matches.'
    },
    {
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      title: 'Speed Dating Success',
      description: 'Be genuine, ask open-ended questions, and have fun! Authenticity leads to better connections.'
    },
    {
      icon: <CheckCircle className="w-5 h-5 text-green-500" />,
      title: 'Technical Setup',
      description: 'Test your camera and microphone before your first session. Good lighting makes a great first impression!'
    }
  ];

  const filteredFAQs = faqSections.map(section => ({
    ...section,
    faqs: section.faqs.filter(
      faq => {
        const answerText = typeof faq.answer === 'string' ? faq.answer : '';
        return (
          faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
          answerText.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
    )
  })).filter(section => section.faqs.length > 0);

  const toggleFAQ = (sectionIndex, faqIndex) => {
    const key = `${sectionIndex}-${faqIndex}`;
    setOpenFAQ(openFAQ === key ? null : key);
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/support`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });

      if (response.ok) {
        const result = await response.json();
        alert('✅ Support request submitted successfully! We\'ll get back to you within 24 hours. Check your email for confirmation.');
        setContactForm({ name: '', email: '', category: '', message: '' });
      } else {
        const error = await response.json();
        alert(`❌ Error submitting request: ${error.detail || 'Please try again later.'}`);
      }
    } catch (error) {
      console.error('Support request error:', error);
      alert('❌ Failed to submit support request. Please check your connection and try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-rose-50 via-white to-pink-50 overflow-x-hidden">
      <SEOHead 
        title="Support & Help Center | Shot Clock Dating"
        description="Get help with AI-powered speed dating, account settings, technical issues, and billing. Comprehensive support for Shot Clock Dating users."
        keywords="speed dating help, dating app support, AI dating assistance, technical support, billing help, account settings"
        url="https://shotclockdating.com/support"
      />

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-rose-600 to-pink-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.h1 
            className="text-4xl md:text-5xl font-bold mb-6"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            How Can We Help You?
          </motion.h1>
          <motion.p 
            className="text-xl text-rose-100 mb-8 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
          >
            Get support for your AI-powered speed dating experience. Find answers, get technical help, or contact our team.
          </motion.p>
          
          {/* Search Bar */}
          <motion.div 
            className="relative max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
          >
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search for help topics, features, or issues..."
              className="w-full pl-12 pr-4 py-4 rounded-xl text-gray-900 text-lg focus:outline-none focus:ring-4 focus:ring-rose-300 shadow-lg"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Quick Tips */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">Quick Tips for Success</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {quickTips.map((tip, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-start mb-3">
                  {tip.icon}
                  <h3 className="text-lg font-semibold text-gray-800 ml-3">{tip.title}</h3>
                </div>
                <p className="text-gray-600">{tip.description}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Contact Options */}
        <motion.div 
          className="mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">Contact Our Support Team</h2>
          <div className="flex justify-center">
            <div className="max-w-md w-full">
            {contactOptions.map((option, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-lg border border-gray-100 text-center">
                <div className={`w-12 h-12 ${option.color} rounded-full flex items-center justify-center text-white mx-auto mb-4`}>
                  {option.icon}
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{option.title}</h3>
                <div className="text-gray-600 mb-4">
                  {typeof option.description === 'string' ? option.description : option.description}
                </div>
                <button 
                  className={`w-full py-3 px-6 ${option.color} text-white rounded-lg font-semibold transition-colors`}
                  onClick={option.actionHandler || (() => {})}
                >
                  {option.action}
                </button>
              </div>
            ))}
            </div>
          </div>
        </motion.div>

        {/* FAQ Sections */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.0, duration: 0.8 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-8 text-center">Frequently Asked Questions</h2>
          
          <div className="grid lg:grid-cols-2 gap-8">
            {(searchTerm ? filteredFAQs : faqSections).map((section, sectionIndex) => (
              <div key={sectionIndex} className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                <div className={`${section.bgColor} p-4 border-b border-gray-100`}>
                  <div className="flex items-center">
                    <span className={section.color}>{section.icon}</span>
                    <h3 className="text-lg font-semibold text-gray-800 ml-3">{section.title}</h3>
                  </div>
                </div>
                
                <div className="p-4">
                  {section.faqs.map((faq, faqIndex) => {
                    const isOpen = openFAQ === `${sectionIndex}-${faqIndex}`;
                    return (
                      <div key={faqIndex} className="border-b border-gray-100 last:border-b-0">
                        <button
                          onClick={() => toggleFAQ(sectionIndex, faqIndex)}
                          className="w-full py-4 px-2 text-left flex items-center justify-between hover:bg-gray-50 rounded-lg transition-colors"
                        >
                          <span className="font-semibold text-gray-800 pr-4">{faq.question}</span>
                          {isOpen ? <ChevronDown className="w-5 h-5 text-gray-500" /> : <ChevronRight className="w-5 h-5 text-gray-500" />}
                        </button>
                        {isOpen && (
                          <div className="px-2 pb-4">
                            <div className="text-gray-600 leading-relaxed">
                              {typeof faq.answer === 'string' ? faq.answer : faq.answer}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Contact Form */}
        <motion.div 
          className="mt-16 bg-white rounded-xl shadow-lg border border-gray-100 p-8"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
        >
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">Still Need Help?</h2>
          <p className="text-gray-600 text-center mb-8">Can't find what you're looking for? Send us a message and we'll get back to you within 24 hours.</p>
          
          <form onSubmit={handleContactSubmit} className="max-w-2xl mx-auto">
            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Name</label>
                <input
                  type="text"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                  value={contactForm.name}
                  onChange={(e) => setContactForm({...contactForm, name: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email</label>
                <input
                  type="email"
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                  value={contactForm.email}
                  onChange={(e) => setContactForm({...contactForm, email: e.target.value})}
                />
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
              <select
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                value={contactForm.category}
                onChange={(e) => setContactForm({...contactForm, category: e.target.value})}
              >
                <option value="">Select a category</option>
                <option value="technical">Technical Issues</option>
                <option value="account">Account & Profile</option>
                <option value="billing">Billing & Subscriptions</option>
                <option value="safety">Safety & Privacy</option>
                <option value="features">Features & AI</option>
                <option value="other">Other</option>
              </select>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Message</label>
              <textarea
                required
                rows="5"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500"
                placeholder="Please describe your issue or question in detail..."
                value={contactForm.message}
                onChange={(e) => setContactForm({...contactForm, message: e.target.value})}
              ></textarea>
            </div>
            
            <button
              type="submit"
              className="w-full bg-gradient-to-r from-rose-600 to-pink-600 text-white py-4 px-8 rounded-lg font-semibold hover:from-rose-700 hover:to-pink-700 transition-all duration-300 shadow-lg"
            >
              Send Support Request
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default SupportPage;